
import { Box, Typography, Dialog, useTheme, Paper, Chip } from '@mui/material';
import { keyframes, css } from '@emotion/react';
import { AiMessage, CustomerReference, ProductDto } from "../../../api-clients/composition";
import CustomerCard from "./cards/CustomerCard";
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';

import ProductCard from "./cards/ProductCard";
import React, { useEffect, useRef, useState, memo, useMemo } from "react";
import { useRedirect } from 'react-admin';
import { useAppDataContext } from "../../../framework/app/app.data.context";
// MUI Icons for chat status
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';


export interface AnswerProps {
  customers: Map<string, CustomerReference>;
  formatActions: (text: string) => string;
  loading: boolean;
  messages: AiMessage[];
  products: Map<string, ProductDto>;
  runStatus: string;
  isDrawer?: boolean;
  entityCardsByMessage?: Map<string, Record<string, string>>;
  insightMode?: boolean; // If true, disable auto-scroll (read-only insight view)
  isVisible?: boolean; // If false, skip expensive processing when component is not visible
}

export interface LinkReference {
  id: string;
  linkPosition?: { left: number; top: number };
  type: string;
  messageId?: string;  // Track which message this reference came from
}

interface QuickViewProps {
  onClose: () => void;
  quickViewItem: LinkReference | null;
  entityCardsByMessage?: Map<string, Record<string, string>>;
}

function AnswerSection({
  customers,
  formatActions,
  loading,
  messages,
  products,
  runStatus,
  isDrawer = true,
  entityCardsByMessage = new Map(),
  insightMode = false,
  isVisible = true, // Default to true for backward compatibility
}: AnswerProps) {
  const theme = useTheme();
  const messageListRef = useRef<HTMLDivElement>(null);
  const [lightBoxIsOpen, setLightBoxIsOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>("");
  const [quickViewLink, setQuickViewLink] = useState<LinkReference | null>(
    null
  );
  const quickViewRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const appDataContext = useAppDataContext();
  const redirect = useRedirect();


  // handle quick view auto close 
  useEffect(() => {
    // Attach the listener when the component mounts
    document.addEventListener("mousedown", handleQuickViewLostFocus);

    // Detach the listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleQuickViewLostFocus);
    };
  }, []);

  // scroll to last message - disabled in insight mode
  useEffect(() => {
    if (insightMode) return; // Don't auto-scroll in insight mode
    
    if (messageListRef && messageListRef.current) {
      const lastChildElement = messageListRef.current?.lastElementChild;
      lastChildElement?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [messages, insightMode]);

  // scroll to the loading indicator - disabled in insight mode
  useEffect(() => {
    if (insightMode) return; // Don't auto-scroll in insight mode
    
    if (loading && loadingRef.current) {
      loadingRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });;
    }
  }, [loading, insightMode]); // Dependency array includes 'loading' prop

  const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

  const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

  // animation to render the ... to simulate a pending chat response
  const TypingAnimation = ({ statusMessage }: { statusMessage: string }) => {
    const theme = useTheme();

    console.log('TypingAnimation statusMessage', statusMessage);

    // Remove any emojis from the status message and clean up whitespace
    const cleanMessage = statusMessage.length 
      ? statusMessage.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim()
      : "Analyzing";
    const displayMessage = cleanMessage || "Analyzing";

    // Helper function to get the appropriate icon based on status message
    const getStatusIcon = (statusMessage: string) => {
      const message = statusMessage.toLowerCase();
      const iconStyle = { 
        fontSize: '1.2rem', 
        color: theme.palette.common.white,
        animation: `${spin} 2s linear infinite`
      };
      
      if (message.includes('thinking')) {
        return <PsychologyIcon sx={iconStyle} />;
      } else if (message.includes('processing')) {
        return <AutorenewIcon sx={iconStyle} />;
      } else if (message.includes('analyzing')) {
        return <AnalyticsIcon sx={iconStyle} />;
      } else if (message.includes('reviewing')) {
        return <RateReviewIcon sx={iconStyle} />;
      } else if (message.includes('executing')) {
        return <PlayArrowIcon sx={iconStyle} />;
      } else if (message.includes('loading')) {
        return <CloudDownloadIcon sx={iconStyle} />;
      } else if (message.includes('starting')) {
        return <HourglassEmptyIcon sx={{ ...iconStyle, animation: `${pulse} 1.5s ease-in-out infinite` }} />;
      }
      
      // Default icon for any other status
      return <AutorenewIcon sx={iconStyle} />;
    };

    return (
      <Paper
        elevation={3}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          maxWidth: 'fit-content',
          mt: 2,
        }}
      >
        {getStatusIcon(displayMessage)}
        <Typography variant="body2">{displayMessage}</Typography>
      </Paper>
    );
  };
  //

  // get array of JSX elements representing Cards representing details of items referenced in the messages
  const ContentCards = ({ message }): JSX.Element | null => {
    if (!message.metaData) return null;
    const elements: JSX.Element[] = [];

    type Reference = { key: string; value: string };
    const references: Reference[] = JSON.parse(message.metaData);

    if (references && references.length) {
      references.forEach((reference, index) => {
        const key = reference.key;
        const value = reference.value;

        if (key === "product") {
          const product = products.get(value);
          if (product) {
            elements.push(
              <React.Fragment key={`product-${value}-${index}`}>
                {ProductCard({ product })}
              </React.Fragment>
            );
          }
        } else if (key === "customer") {
          const customer = customers.get(value);
          if (customer) {
            elements.push(
              <React.Fragment key={`customer-${value}-${index}`}>
                <CustomerCard customer={customer} />
              </React.Fragment>
            );
          }
        }
      });
    }

    return elements.length ? <>{elements}</> : null;
  };

  // support viweing images embedded in messages in lightbox
  const handleImageViewClick = (event) => {
    const imgSrc = event.target.src;
    setImageSrc(imgSrc);
    setLightBoxIsOpen(true);
  };

  // support data references in messages like product or customer being clickable inline to show details inline
  const handleQuickViewLinkClick = async (type, id, e, messageId?: string) => {
    e.preventDefault();
    
    console.log('handleQuickViewLinkClick:', type, id, 'from message:', messageId);
    
    // Get position of clicked element for positioning the QuickView card
    const rect = e.currentTarget.getBoundingClientRect();
    
    console.log('🎯 Link clicked at position:');
    console.log('   left:', rect.left);
    console.log('   top:', rect.top);
    console.log('   bottom:', rect.bottom);
    console.log('   right:', rect.right);
    console.log('   width:', rect.width);
    console.log('   height:', rect.height);
    
    // Show QuickView card at click position
    // Use fixed positioning (relative to viewport) not absolute
    setQuickViewLink({
      id: id,
      linkPosition: { 
        left: rect.left,  // Viewport-relative position
        top: rect.bottom + 5  // Just below the link
      },
      type: type,
      messageId: messageId,  // Include messageId to lookup entity cards
    });
    
    // TODO: Optionally load data from dataProvider if not in maps
    // For now, QuickView will show loading state or data from maps
  };

  const handleQuickViewLostFocus = (event) => {
    if (quickViewRef.current && !quickViewRef.current.contains(event.target)) {
      setQuickViewLink(null);
    }
  };

  // Generic QuickView component for any entity type
  const QuickView = ({ onClose, quickViewItem, entityCardsByMessage }: QuickViewProps) => {

    if (!quickViewItem) return null;

    console.log('🎨 QuickView rendering with position:', quickViewItem.linkPosition);

    let card: JSX.Element | null = null;

    // First priority: Check for AI-generated markdown card
    if (quickViewItem.messageId && entityCardsByMessage) {
      console.log(`🔍 QuickView: Looking for card`);
      console.log(`   Entity: ${quickViewItem.type}:${quickViewItem.id}`);
      console.log(`   Message ID: ${quickViewItem.messageId}`);
      console.log(`   Total messages with cards: ${entityCardsByMessage.size}`);
      console.log(`   Available message IDs: ${Array.from(entityCardsByMessage.keys()).join(', ')}`);
      console.log(`   🔗 DEBUG: Full entityCardsByMessage Map:`, Array.from(entityCardsByMessage.entries()).map(([k, v]) => ({ messageId: k, cardKeys: Object.keys(v) })));
      
      const messageCards = entityCardsByMessage.get(quickViewItem.messageId);
      console.log(`   Cards for this message: ${messageCards ? Object.keys(messageCards).join(', ') : 'NONE'}`);
      console.log(`   🔗 DEBUG: Lookup result for messageId "${quickViewItem.messageId}":`, messageCards ? 'FOUND' : 'NOT FOUND');
      if (messageCards) {
        console.log(`   🔗 DEBUG: Full messageCards object:`, JSON.stringify(messageCards, null, 2));
      }
      
      const entityKey = `${quickViewItem.type}:${quickViewItem.id}`;
      console.log(`   🔗 DEBUG: Looking for entityKey: "${entityKey}"`);
      const markdownCard = messageCards?.[entityKey];
      
      if (markdownCard) {
        console.log(`✅ Found markdown card for ${entityKey}!`);
        console.log(`   Markdown content: ${markdownCard.substring(0, 100)}...`);
        
        // Parse markdown with tables and lists for display
        const renderMarkdown = (markdown: string) => {
          console.log('🎨 Rendering markdown, total length:', markdown.length);
          
          // Strip citations in various formats before processing
          markdown = markdown.replace(/【.*?】/g, ''); // Japanese brackets format
          markdown = markdown.replace(/☐cite☐[^☐]*☐/g, ''); // Box character format with cite
          markdown = markdown.replace(/☐turn\d+[a-z]+\d*☐/g, ''); // Box character format with turn references
          markdown = markdown.replace(/cite(turn\d+[a-z]+\d*)+/g, ''); // Text citations: citeturn0search13
          markdown = markdown.replace(/turn\d+[a-z]+\d*/g, ''); // Turn references: turn0news12
          markdown = markdown.replace(/\bfilecite\b/gi, ''); // Standalone filecite (case insensitive)
          markdown = markdown.replace(/\bfile-\d+-cite\b/gi, ''); // file-1-cite, file-2-cite
          markdown = markdown.replace(/\s+cite\d*\s*/gi, ' '); // cite, cite0, cite1 with surrounding spaces
          
          // Split on actual newlines, not escaped backslash-n
          const lines = markdown.split(/\\n|\n/);
          console.log(`📋 Split into ${lines.length} lines`);
          
          const elements: any[] = [];
          let i = 0;
          
          while (i < lines.length) {
            const line = lines[i];
            
            // Handle markdown tables
            if (line.includes('|') && i + 1 < lines.length && lines[i + 1].includes('---')) {
              // Table detected - collect all table rows
              const tableRows: string[] = [];
              let j = i;
              while (j < lines.length && lines[j].includes('|')) {
                tableRows.push(lines[j]);
                j++;
                if (j < lines.length && lines[j].includes('---')) {
                  j++; // Skip separator row
                }
              }
              
              // Parse table
              const headers = tableRows[0].split('|').map(h => h.trim()).filter(h => h);
              const dataRows = tableRows.slice(1).filter(r => !r.includes('---'));
              
              elements.push(
                <Box key={`table-${i}`} sx={{ mb: 1, overflowX: 'auto' }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: '0.75rem'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        {headers.map((h, idx) => (
                          <th key={idx} style={{ 
                            padding: '4px 8px', 
                            textAlign: 'left',
                            fontWeight: 600,
                            color: theme.palette.text.primary
                          }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dataRows.map((row, rowIdx) => {
                        const cells = row.split('|').map(c => c.trim()).filter(c => c);
                        return (
                          <tr key={rowIdx}>
                            {cells.map((cell, cellIdx) => {
                              // Check if cell contains markdown image
                              const imgMatch = cell.match(/!\[([^\]]*)\]\(([^)]+)\)/);
                              
                              return (
                                <td key={cellIdx} style={{ 
                                  padding: '4px 8px',
                                  color: theme.palette.text.secondary
                                }}>
                                  {imgMatch ? (
                                    // Render image in table cell
                                    <img 
                                      src={imgMatch[2]} 
                                      alt={imgMatch[1]}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '4px',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  ) : (
                                    // Regular text with bold, links, emails, and phone numbers
                                    <span dangerouslySetInnerHTML={{ 
                                      __html: (() => {
                                        let processed = cell.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                                        
                                        // Auto-linkify URLs with shortened display text
                                        processed = processed.replace(
                                          /(https?:\/\/[^\s<>"]+)/g,
                                          (match) => {
                                            let displayText = match;
                                            try {
                                              const url = new URL(match);
                                              const path = url.pathname + url.search + url.hash;
                                              if (match.length > 30) {
                                                displayText = url.hostname + (path.length > 15 ? path.substring(0, 12) + '...' : path);
                                              }
                                            } catch (e) {
                                              if (match.length > 30) {
                                                displayText = match.substring(0, 27) + '...';
                                              }
                                            }
                                            return `<a href="${match}" target="_blank" rel="noopener noreferrer" style="color: ${theme.palette.primary.main}; text-decoration: underline;">${displayText}</a>`;
                                          }
                                        );
                                        
                                        // Auto-linkify emails
                                        processed = processed.replace(
                                          /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
                                          '<a href="mailto:$1" style="color: ' + theme.palette.primary.main + '; text-decoration: underline;">$1</a>'
                                        );
                                        
                                        // Auto-linkify phone numbers
                                        processed = processed.replace(
                                          /(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})/g,
                                          (match) => {
                                            const cleanNumber = match.replace(/\D/g, '');
                                            return `<a href="tel:+1${cleanNumber}" style="color: ${theme.palette.primary.main}; text-decoration: underline;">${match}</a>`;
                                          }
                                        );
                                        
                                        return processed;
                                      })()
                                    }} />
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              );
              
              i = j;
              continue;
            }
            
            // Handle unordered lists: - item
            if (line.trim().startsWith('- ')) {
              const listItems: string[] = [];
              let j = i;
              while (j < lines.length && lines[j].trim().startsWith('- ')) {
                listItems.push(lines[j].trim().substring(2));
                j++;
              }
              
              elements.push(
                <ul key={`ul-${i}`} style={{ 
                  margin: '4px 0', 
                  paddingLeft: '20px',
                  fontSize: '0.875rem'
                }}>
                  {listItems.map((item, idx) => {
                    let processed = item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                    
                    // Auto-linkify URLs with shortened display text
                    processed = processed.replace(
                      /(https?:\/\/[^\s<>"]+)/g,
                      (match) => {
                        let displayText = match;
                        try {
                          const url = new URL(match);
                          const path = url.pathname + url.search + url.hash;
                          if (match.length > 40) {
                            displayText = url.hostname + (path.length > 20 ? path.substring(0, 17) + '...' : path);
                          }
                        } catch (e) {
                          if (match.length > 40) {
                            displayText = match.substring(0, 37) + '...';
                          }
                        }
                        return `<a href="${match}" target="_blank" rel="noopener noreferrer" style="color: ${theme.palette.primary.main}; text-decoration: underline;">${displayText}</a>`;
                      }
                    );
                    
                    // Auto-linkify emails
                    processed = processed.replace(
                      /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
                      '<a href="mailto:$1" style="color: ' + theme.palette.primary.main + '; text-decoration: underline;">$1</a>'
                    );
                    
                    // Auto-linkify phone numbers
                    processed = processed.replace(
                      /(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})/g,
                      (match) => {
                        const cleanNumber = match.replace(/\D/g, '');
                        return `<a href="tel:+1${cleanNumber}" style="color: ${theme.palette.primary.main}; text-decoration: underline;">${match}</a>`;
                      }
                    );
                    
                    return (
                      <li key={idx} style={{ marginBottom: '2px' }}>
                        <span dangerouslySetInnerHTML={{ __html: processed }} />
                      </li>
                    );
                  })}
                </ul>
              );
              
              i = j;
              continue;
            }
            
            // Handle markdown images: ![alt](url)
            const imgMatch = line.match(/!\[([^\]]*)\]\(([^)]+)\)/);
            if (imgMatch) {
              const alt = imgMatch[1];
              const url = imgMatch[2];
              elements.push(
                <Box 
                  key={`img-${i}`}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    mb: 1
                  }}
                >
                  <img 
                    src={url} 
                    alt={alt}
                    style={{ 
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: `2px solid ${theme.palette.primary.main}`,
                    }} 
                  />
                </Box>
              );
              i++;
              continue;
            }
            
            // Handle markdown headers: ## Header or ### Header
            if (line.startsWith('### ')) {
              elements.push(
                <Typography key={`h3-${i}`} variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, mt: 0.5 }}>
                  {line.replace('### ', '')}
                </Typography>
              );
              i++;
              continue;
            }
            
            if (line.startsWith('## ')) {
              elements.push(
                <Typography key={`h2-${i}`} variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {line.replace('## ', '')}
                </Typography>
              );
              i++;
              continue;
            }
            
            // Handle bold text, links, emails, and phone numbers
            let processedLine = line
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .replace(/\*(.*?)\*/g, '<em>$1</em>');
            
            // Auto-linkify URLs with shortened display text
            processedLine = processedLine.replace(
              /(https?:\/\/[^\s<>"]+)/g,
              (match) => {
                // Shorten URL for display (show domain + ellipsis if long)
                let displayText = match;
                try {
                  const url = new URL(match);
                  const path = url.pathname + url.search + url.hash;
                  if (match.length > 40) {
                    displayText = url.hostname + (path.length > 20 ? path.substring(0, 17) + '...' : path);
                  }
                } catch (e) {
                  // If URL parsing fails, just truncate
                  if (match.length > 40) {
                    displayText = match.substring(0, 37) + '...';
                  }
                }
                return `<a href="${match}" target="_blank" rel="noopener noreferrer" style="color: ${theme.palette.primary.main}; text-decoration: underline;">${displayText}</a>`;
              }
            );
            
            // Auto-linkify emails
            processedLine = processedLine.replace(
              /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
              '<a href="mailto:$1" style="color: ' + theme.palette.primary.main + '; text-decoration: underline;">$1</a>'
            );
            
            // Auto-linkify phone numbers (supports various formats)
            processedLine = processedLine.replace(
              /(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})/g,
              (match) => {
                const cleanNumber = match.replace(/\D/g, '');
                return `<a href="tel:+1${cleanNumber}" style="color: ${theme.palette.primary.main}; text-decoration: underline;">${match}</a>`;
              }
            );
            
            if (processedLine.trim()) {
              elements.push(
                <div 
                  key={`line-${i}`} 
                  dangerouslySetInnerHTML={{ __html: processedLine }}
                  style={{ marginBottom: '4px', fontSize: '0.875rem' }}
                />
              );
            }
            
            i++;
          }
          
          console.log(`✅ Rendered ${elements.length} markdown elements`);
          return elements;
        };
        
        card = (
          <Box sx={{ 
            '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
            '& strong': { color: theme.palette.text.primary, fontWeight: 600 },
            '& em': { fontStyle: 'italic' },
            fontSize: '0.875rem',
            lineHeight: 1.6,
          }}>
            {renderMarkdown(markdownCard)}
          </Box>
        );
      }
    }

    // Second priority: Try to get data from existing maps (customer/product cards)
    if (!card && quickViewItem.type === "product" && products.has(quickViewItem.id)) {
      const product = products.get(quickViewItem.id);
      if (product) {
        card = ProductCard({ product });
      }
    } else if (!card && quickViewItem.type === "customer" && customers.has(quickViewItem.id)) {
      const customer = customers.get(quickViewItem.id);
      if (customer) {
        card = <CustomerCard customer={customer} />;
      }
    }

    // Fallback: Generic placeholder card for any entity type
    if (!card) {
      card = (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <AutorenewIcon 
            sx={{ 
              fontSize: '2rem', 
              color: theme.palette.primary.main,
              animation: `${spin} 1s linear infinite`,
              mb: 1
            }} 
          />
          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {quickViewItem.type}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            ID: {quickViewItem.id}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
            Details not yet available
          </Typography>
        </Box>
      );
    }

    // Calculate viewport-aware position to keep card onscreen
    const cardWidth = 300;
    const cardMaxHeight = 400;
    const glowExtension = 50; // Extra space for the glowing box-shadow effect
    const padding = 40; // Increased padding from viewport edges
    
    let computedLeft = quickViewItem.linkPosition?.left ?? 0;
    let computedTop = quickViewItem.linkPosition?.top ?? 0;
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Always check if card would overflow right edge, accounting for glow effect
    const visualRightEdge = computedLeft + cardWidth + glowExtension;
    if (visualRightEdge + padding > viewportWidth) {
      // Move card to the left to fit within viewport, accounting for glow
      computedLeft = viewportWidth - cardWidth - glowExtension - padding;
    }
    
    // Ensure it doesn't go negative on the left side (accounting for left-side glow too)
    computedLeft = Math.max(padding + glowExtension, computedLeft);
    
    // Adjust vertical position if card would overflow bottom edge
    if (computedTop + cardMaxHeight + padding > viewportHeight) {
      // Try to position above the link instead, or at minimum padding from bottom
      computedTop = Math.max(padding, viewportHeight - cardMaxHeight - padding);
    }
    
    console.log('🎨 QuickView will render at:');
    console.log('   left:', computedLeft, 'px');
    console.log('   top:', computedTop, 'px');
    console.log('   position: fixed');
    console.log('   viewport:', viewportWidth, 'x', viewportHeight);
    
    return (
      <Paper
        ref={quickViewRef}
        style={{
          // Use inline styles for positioning (higher specificity than sx)
          position: 'fixed',
          left: `${computedLeft}px`,
          top: `${computedTop}px`,
          width: `${cardWidth}px`,
          maxHeight: `${cardMaxHeight}px`,
          overflowY: 'auto',
          zIndex: 9999,
        }}
        sx={{
          // Use sx for theme-aware styling
          p: 2.5,
          borderRadius: 3,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          // Enhanced background with slight gradient for depth
          background: 'linear-gradient(145deg, rgba(40, 40, 40, 0.98), rgba(30, 30, 30, 0.95))',
          // Multiple layered shadows for floating effect
          boxShadow: `
            0 0 20px 2px rgba(0, 123, 255, 0.3),
            0 0 40px 4px rgba(0, 123, 255, 0.15),
            0 20px 60px rgba(0, 0, 0, 0.6),
            0 10px 30px rgba(0, 0, 0, 0.4),
            inset 0 1px 2px rgba(255, 255, 255, 0.1)
          `,
          // Glowing border
          border: '1.5px solid rgba(0, 123, 255, 0.4)',
          // Add a subtle inner glow
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 3,
            padding: '1.5px',
            background: 'linear-gradient(145deg, rgba(0, 123, 255, 0.3), rgba(0, 60, 130, 0.2))',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          },
        }}
      >
        {card}
      </Paper>
    );
  };

  // Extract meaningful display name from entity card markdown or message context
  const getDisplayName = (type: string, id: string, messageId?: string, messageText?: string) => {
    const entityKey = `${type}:${id}`;
    
    // First, try to extract name from entity card markdown if available
    if (messageId && entityCardsByMessage) {
      const messageCards = entityCardsByMessage.get(messageId);
      const markdownCard = messageCards?.[entityKey];
      
      if (markdownCard) {
        // Try to extract title/name from markdown
        // Look for markdown headings (## Title or # Title)
        const headingMatch = markdownCard.match(/^#+\s+(.+)$/m);
        if (headingMatch && headingMatch[1]) {
          const title = headingMatch[1].trim();
          // Remove markdown formatting from title
          return title.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '').trim();
        }
        
        // Look for first line that looks like a title (starts with capital, not a bullet, not empty)
        const lines = markdownCard.split(/\\n|\n/).map(l => l.trim()).filter(l => l.length > 0);
        for (const line of lines) {
          // Skip markdown formatting, bullets, etc.
          if (!line.match(/^[-*+\d.]\s/) && // Not a bullet or numbered list
              !line.match(/^\|/) && // Not a table row
              !line.match(/^```/) && // Not a code block
              line.length > 2 && // Has some content
              line[0] === line[0].toUpperCase()) { // Starts with capital
            // Clean up markdown formatting
            const cleanTitle = line
              .replace(/^#+\s+/, '') // Remove heading markers
              .replace(/\*\*/g, '') // Remove bold
              .replace(/\*/g, '') // Remove italic
              .replace(/`/g, '') // Remove code
              .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Convert links to text
              .trim();
            
            if (cleanTitle.length > 0 && cleanTitle.length < 100) { // Reasonable length
              return cleanTitle;
            }
          }
        }
      }
    }
    
    // Fallback: Try to extract name from surrounding text in the message
    if (messageText) {
      // Look for pattern like "Name ${type:id}" or "${type:id} - Name"
      const beforePattern = new RegExp(`([A-Z][^${'$'}]+)\\s*\\$\\{${type}:${id}\\}`, 'i');
      const afterPattern = new RegExp(`\\$\\{${type}:${id}\\}[^${'$'}]*[-–]\\s*([A-Z][^${'$'}]+)`, 'i');
      
      const beforeMatch = messageText.match(beforePattern);
      if (beforeMatch && beforeMatch[1]) {
        const name = beforeMatch[1].trim();
        // Clean up common prefixes/suffixes
        return name.replace(/^(the|a|an)\s+/i, '').substring(0, 50);
      }
      
      const afterMatch = messageText.match(afterPattern);
      if (afterMatch && afterMatch[1]) {
        const name = afterMatch[1].trim();
        return name.substring(0, 50);
      }
    }
    
    // Final fallback: return "view"
    return `view`;
  }

  // A message post processor to handle formatting messages that may include links, object references, and action keys
  const MessageFormatter = ({ message, messageId, onReferenceClick }) => {
    // Ensure messageId is always a valid string for key generation
    const safeMessageId = messageId || `unknown_${Date.now()}`;
    
    // Function to replace encoded references with JSX
    const formatMessage = (msg: string) => {
      // Skip expensive processing when component is not visible (e.g., when window loses focus)
      // This prevents unnecessary work when react-query refetches data on focus
      if (!isVisible) {
        // Return a simplified version without processing entity references
        // This avoids expensive regex operations and logging when not visible
        return <>{formatActions(msg)}</>;
      }
      
      // Get entityCards for this message if available
      const messageEntityCards = entityCardsByMessage?.get(safeMessageId);
      // Regex pattern for entity references - unified format: ${type:id}
      // IMPORTANT: The type capture group must NOT include { or } to prevent matching across multiple tokens
      const refPattern = /\$\{([^:{}\s]+):([^}]+)\}/g;  // Format: ${type:id} - type cannot contain : { } or whitespace

      // First format any action keys in the message
      msg = formatActions(msg);

      // Strip citations in various formats
      msg = msg.replace(/【.*?】/g, ''); // Japanese brackets format
      msg = msg.replace(/☐cite☐[^☐]*☐/g, ''); // Box character format with cite
      msg = msg.replace(/☐turn\d+[a-z]+\d*☐/g, ''); // Box character format with turn references
      msg = msg.replace(/cite(turn\d+[a-z]+\d*)+/g, ''); // Text citations: citeturn0search13
      msg = msg.replace(/turn\d+[a-z]+\d*/g, ''); // Turn references: turn0news12
      msg = msg.replace(/\bfilecite\b/gi, ''); // Standalone filecite (case insensitive)
      msg = msg.replace(/\bfile-\d+-cite\b/gi, ''); // file-1-cite, file-2-cite
      msg = msg.replace(/\s+cite\d*\s*/gi, ' '); // cite, cite0, cite1 with surrounding spaces

      // Special handling for headline and byline entity references - replace with content inline
      // These should NOT be clickable links, but should be replaced with the actual content
      if (messageEntityCards) {
        // Replace ${headline:headline} with actual headline content
        const headlineCard = messageEntityCards['headline:headline'];
        if (headlineCard) {
          // Extract text content from markdown (remove formatting, take first meaningful line)
          const headlineText = headlineCard
            .split(/\\n|\n/)[0] // Get first line
            .replace(/^#+\s+/, '') // Remove heading markers
            .replace(/\*\*/g, '') // Remove bold
            .replace(/\*/g, '') // Remove italic
            .replace(/`/g, '') // Remove code
            .trim();
          if (headlineText) {
            msg = msg.replace(/\$\{headline:headline\}/g, headlineText);
          }
        }
        
        // Replace ${byline:byline} with actual byline content
        const bylineCard = messageEntityCards['byline:byline'];
        if (bylineCard) {
          // Extract text content from markdown (remove formatting, take first meaningful line)
          const bylineText = bylineCard
            .split(/\\n|\n/)[0] // Get first line
            .replace(/^#+\s+/, '') // Remove heading markers
            .replace(/\*\*/g, '') // Remove bold
            .replace(/\*/g, '') // Remove italic
            .replace(/`/g, '') // Remove code
            .trim();
          if (bylineText) {
            msg = msg.replace(/\$\{byline:byline\}/g, bylineText);
          }
        }
      }

      // Process the message and convert special patterns to React elements
      const elements: any[] = [];
      let lastIndex = 0;
      let keyCounter = 0;

      // Process markdown images: ![alt](url)
      const imgPattern = /!\[([^\]]*)\]\(([^)]+)\)/g;
      let imgMatch;
      const imagePositions: Array<{start: number, end: number, element: JSX.Element}> = [];
      
      while ((imgMatch = imgPattern.exec(msg)) !== null) {
        const alt = imgMatch[1];
        const url = imgMatch[2];
        imagePositions.push({
          start: imgMatch.index,
          end: imgMatch.index + imgMatch[0].length,
          element: (
            <img
              key={`${safeMessageId}-img-${keyCounter++}`}
              src={url}
              alt={alt}
              onClick={handleImageViewClick}
              width="90%"
              style={{ borderRadius: '8px', margin: '8px 0' }}
            />
          )
        });
      }

      // Process markdown links: [text](url)
      const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
      let linkMatch;
      const linkPositions: Array<{start: number, end: number, element: JSX.Element}> = [];
      
      while ((linkMatch = linkPattern.exec(msg)) !== null) {
        const text = linkMatch[1];
        const url = linkMatch[2];
        linkPositions.push({
          start: linkMatch.index,
          end: linkMatch.index + linkMatch[0].length,
          element: (
            <a
              key={`${safeMessageId}-link-${keyCounter++}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
              }}
            >
              {text}
            </a>
          )
        });
      }

      // Process entity references: ${type:id} - all entity types use the same format
      let refMatch;
      const refPositions: Array<{start: number, end: number, element: JSX.Element}> = [];
      
      while ((refMatch = refPattern.exec(msg)) !== null) {
        const type = refMatch[1];
        const id = refMatch[2];
        
        // Get display name with context from entity cards and message text
        const displayName = getDisplayName(type, id, safeMessageId, msg);
        
        refPositions.push({
          start: refMatch.index,
          end: refMatch.index + refMatch[0].length,
          element: (
            <a
              key={`${safeMessageId}-ref-${keyCounter++}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                console.log(`🖱️ Clicked ${type}:${id} from message ${safeMessageId}`);
                onReferenceClick(type, id, e, safeMessageId);  // Pass safeMessageId
              }}
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {displayName}
            </a>
          )
        });
      }

      // Combine all positions and sort by start index
      const allPositions = [...imagePositions, ...linkPositions, ...refPositions]
        .sort((a, b) => a.start - b.start);

      // Build final result by iterating through positions
      for (const position of allPositions) {
        // Add text before this element
        if (lastIndex < position.start) {
          const textBefore = msg.substring(lastIndex, position.start);
          if (textBefore) {
            elements.push(
              <React.Fragment key={`${safeMessageId}-text-${keyCounter++}`}>
                {textBefore}
              </React.Fragment>
            );
          }
        }
        
        // Add the element
        elements.push(position.element);
        lastIndex = position.end;
      }

      // Add any remaining text after the last element
      if (lastIndex < msg.length) {
        const textAfter = msg.substring(lastIndex);
        if (textAfter) {
          elements.push(
            <React.Fragment key={`${safeMessageId}-text-${keyCounter++}`}>
              {textAfter}
            </React.Fragment>
          );
        }
      }

      // Message processed - elements array now contains the formatted content

      // If no elements, return the original message as a single keyed element with unique messageId-based key
      if (elements.length === 0) {
        return [<React.Fragment key={`${safeMessageId}-content-0`}>{msg}</React.Fragment>];
      }

      return elements;
    };



    return <>{formatMessage(message)}</>;
  };

  // Helper to get file icon based on extension
  const getFileIcon = (extension?: string) => {
    if (!extension) return <InsertDriveFileIcon />;
    const ext = extension.toLowerCase();
    if (ext === 'pdf') return <PictureAsPdfIcon />;
    if (['doc', 'docx', 'txt', 'csv', 'xlsx'].includes(ext)) return <DescriptionIcon />;
    return <InsertDriveFileIcon />;
  };

  return (
    <>
      <div className={`chat-container ${isDrawer ? "drawer-mode" : "wide-mode"}`} >
        <Box
          ref={messageListRef}
          sx={{
            paddingLeft: isDrawer ? 0 : 20,
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflowY: 'auto',
            p: 3,
            gap: 2,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015))',
            boxShadow: `
              inset 0 1px 3px rgba(255,255,255,0.04),
              0 8px 30px rgba(0,0,0,0.4)
            `,
            borderRadius: 4,
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            position: 'relative',  // Added for QuickView positioning

          }}
        >
          {messages.map((message, messageIndex) => {
            const isAssistant = message.role === 'assistant';
            // Use message.id if available, otherwise create a unique fallback key
            const messageKey = message.id || `msg_fallback_${messageIndex}_${message.createdAt || Date.now()}`;
            
            return (
              <motion.div
                key={messageKey}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    position: 'relative',
                    background: isAssistant
                      ? 'linear-gradient(145deg, rgba(30,30,30,0.6), rgba(30,30,30,0.3))'
                      : 'linear-gradient(145deg, rgba(0,123,255,0.25), rgba(0,60,130,0.08))',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    boxShadow: `
                    0 4px 20px rgba(0, 0, 0, 0.3),
                    0 8px 30px rgba(0, 0, 0, 0.25),
                    inset 0 1px 2px rgba(255, 255, 255, 0.06)
                  `,
                    color: theme.palette.text.primary,
                    fontWeight: 525,
                    lineHeight: 2,
                    fontSize: '1.1rem',
                    alignSelf: isAssistant ? 'flex-start' : 'flex-end',
                    maxWidth: '90%',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `
                      0 6px 40px rgba(0, 0, 0, 0.45),
                      inset 0 2px 4px rgba(255, 255, 255, 0.15)
                    `,
                    },
                  }}
                >
                  {/* Display attached files first */}
                  {message.fileReferences && message.fileReferences.length > 0 && (
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {message.fileReferences.map((fileRef, idx) => {
                        const isImage = fileRef.fileExtension && 
                          ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileRef.fileExtension.toLowerCase());
                        
                        if (isImage && fileRef.url) {
                          // Show image preview
                          return (
                            <Box
                              key={idx}
                              component="img"
                              src={fileRef.url}
                              alt={fileRef.name}
                              onClick={handleImageViewClick}
                              sx={{
                                maxWidth: '200px',
                                maxHeight: '200px',
                                borderRadius: 2,
                                cursor: 'pointer',
                                objectFit: 'cover',
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                '&:hover': {
                                  border: '2px solid rgba(255, 255, 255, 0.4)',
                                },
                              }}
                            />
                          );
                        } else {
                          // Show file chip for non-images
                          return (
                            <Chip
                              key={idx}
                              icon={getFileIcon(fileRef.fileExtension)}
                              label={fileRef.name}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                color: theme.palette.text.primary,
                                '& .MuiChip-icon': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          );
                        }
                      })}
                    </Box>
                  )}
                  
                  {/* Display message content */}
                  {message.content.map((content, idx) => {
                    // Use message key + content index for unique keys across all messages
                    const contentKey = `${messageKey}-content-${idx}`;
                    
                    if (content.type === 'text' && content.text?.length) {
                      return (
                        <MessageFormatter
                          key={contentKey}
                          message={content.text}
                          messageId={messageKey}
                          onReferenceClick={handleQuickViewLinkClick}
                        />
                      );
                    } else if (content.type === 'image_file' && content.fileReferences?.[0]?.url) {
                      // Additional image content (from content array)
                      return (
                        <img
                          key={contentKey}
                          alt="ai"
                          onClick={handleImageViewClick}
                          src={content.fileReferences[0].url}
                          style={{ 
                            maxWidth: '90%', 
                            borderRadius: '12px',
                            marginTop: idx > 0 ? '8px' : '0'
                          }}
                        />
                      );
                    }
                    return null;
                  })}
                </Paper>
              </motion.div>
            );
          })}
          {loading && <TypingAnimation statusMessage={runStatus} />}
          
        </Box>
      </div>
      
      {/* Render QuickView in a portal at document body level to avoid CSS positioning issues */}
      {quickViewLink && createPortal(
        <QuickView 
          quickViewItem={quickViewLink} 
          onClose={() => setQuickViewLink(null)}
          entityCardsByMessage={entityCardsByMessage}
        />,
        document.body
      )}
    </>
  );
}

// Memoize AnswerSection to prevent re-renders when parent state changes
// (e.g., headline/byline rotation in AiInsightCard)
export default memo(AnswerSection, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props change
  if (prevProps.messages !== nextProps.messages) return false;
  if (prevProps.loading !== nextProps.loading) return false;
  if (prevProps.runStatus !== nextProps.runStatus) return false;
  if (prevProps.entityCardsByMessage !== nextProps.entityCardsByMessage) return false;
  if (prevProps.isVisible !== nextProps.isVisible) return false;
  if (prevProps.insightMode !== nextProps.insightMode) return false;
  // All relevant props are the same - don't re-render
  return true;
});
